<template>
  <!-- Speed, as a menu rather than − / +: the choice is one of six values, and seeing them all at once
       is quicker than stepping through them to find the one you wanted. Built here and not as a
       `v-menu`, which would open behind the dialog and hand its Escape and arrow keys to the
       walkthrough. -->
  <div ref="rootRef" class="vhelp__speed" @keydown="onKeydown" @focusout="onFocusOut">
    <button
      ref="buttonRef"
      type="button"
      class="vhelp__speed-button"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="t('help.player.speed', { speed: speedLabel })"
      @click="toggle"
    >
      {{ speedLabel }}
      <v-icon size="14" class="vhelp__speed-chevron" :class="{ 'is-open': open }">mdi-chevron-up</v-icon>
    </button>

    <transition name="vhelp-menu">
      <div v-if="open" ref="menuRef" class="vhelp__speed-menu" role="menu" :aria-label="t('help.player.speed_menu')">
        <button
          v-for="option in OPTIONS"
          :key="option"
          type="button"
          role="menuitemradio"
          class="vhelp__speed-option"
          :class="{ 'is-active': option === speed }"
          :aria-checked="option === speed ? 'true' : 'false'"
          @click="choose(option)"
        >
          <v-icon size="14" class="vhelp__speed-check">{{ option === speed ? "mdi-check" : "" }}</v-icon>
          {{ formatSpeed(option) }}
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
/**
 * The player's speed control. Keeps its keys to itself while it is open — Escape closes the menu, not
 * the walkthrough, and the arrow keys move through the speeds rather than skipping a step — by
 * stopping them before they reach the dialog's keyboard handling.
 */
import { nextTick, onBeforeUnmount, ref, watch } from "vue"
import { SPEEDS, useViewHelp } from "@/composables/viewHelp/useViewHelp"
import { useViewHelpCopy } from "@/composables/viewHelp/useViewHelpCopy"

/** Fastest on top, since the menu opens upwards. */
const OPTIONS = [...SPEEDS].reverse()

const { speed, setSpeed } = useViewHelp()
const { t, formatSpeed, speedLabel } = useViewHelpCopy()

const rootRef = ref<HTMLElement | null>(null)
const buttonRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const open = ref(false)

const options = () => Array.from(menuRef.value?.querySelectorAll<HTMLElement>("[role=menuitemradio]") ?? [])

async function focusOption(which: "active" | number) {
  await nextTick()
  const list = options()
  if (!list.length) return
  const index =
    which === "active"
      ? Math.max(
          0,
          list.findIndex(el => el.getAttribute("aria-checked") === "true")
        )
      : which
  list[(index + list.length) % list.length]?.focus()
}

function close(returnFocus = false) {
  open.value = false
  if (returnFocus) buttonRef.value?.focus()
}

/** Opening moves focus onto the current speed, so the arrow keys work from where the reader is. */
function toggle() {
  open.value = !open.value
  if (open.value) void focusOption("active")
}

function choose(value: number) {
  setSpeed(value)
  close(true)
}

/** The menu's own keys, while it is open. Everything it handles stops here. */
function onKeydown(event: KeyboardEvent) {
  if (!open.value) return
  const current = options().indexOf(document.activeElement as HTMLElement)

  if (event.key === "Tab") {
    // Closes, and lets Tab carry on to the next control — the dialog still keeps it inside itself.
    close()
    return
  }
  if (event.key === "Escape") close(true)
  else if (event.key === "ArrowDown") void focusOption(current + 1)
  else if (event.key === "ArrowUp") void focusOption(current - 1)
  else if (event.key === "Home") void focusOption(0)
  else if (event.key === "End") void focusOption(options().length - 1)
  // Space and Enter press the focused option; ← and → are nobody's while the menu is open.
  else if (![" ", "Enter", "ArrowLeft", "ArrowRight"].includes(event.key)) return

  if (!["Enter", " "].includes(event.key)) event.preventDefault()
  event.stopPropagation()
}

/** Focus leaving the control — to the shortcut list, say — closes the menu behind it. */
function onFocusOut(event: FocusEvent) {
  if (open.value && !rootRef.value?.contains(event.relatedTarget as Node | null)) close()
}

/** A press anywhere outside the control closes the menu. */
function onPressOutside(event: MouseEvent) {
  if (!rootRef.value?.contains(event.target as Node)) close()
}

watch(open, isOpen => {
  if (isOpen) document.addEventListener("mousedown", onPressOutside, true)
  else document.removeEventListener("mousedown", onPressOutside, true)
})

onBeforeUnmount(() => document.removeEventListener("mousedown", onPressOutside, true))
</script>

<style scoped>
.vhelp__speed {
  position: relative;
  flex: none;
}

.vhelp__speed-button {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 3.8em;
  height: 30px;
  padding: 0 6px 0 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.16);
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.vhelp__speed-button:hover,
.vhelp__speed-button[aria-expanded="true"] {
  background: rgba(var(--v-theme-on-surface), 0.06);
  border-color: rgba(var(--v-theme-on-surface), 0.28);
}

.vhelp__speed-button:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.vhelp__speed-chevron {
  opacity: 0.6;
  transition: transform 0.2s ease;
}

.vhelp__speed-chevron.is-open {
  transform: rotate(180deg);
}

/* Opens upwards: the player sits at the bottom of the dialog, and there is no room below it. */
.vhelp__speed-menu {
  position: absolute;
  right: 0;
  bottom: calc(100% + 6px);
  z-index: 2;
  display: flex;
  flex-direction: column;
  min-width: 96px;
  padding: 4px;
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.vhelp__speed-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;
}

.vhelp__speed-option:hover,
.vhelp__speed-option:focus-visible {
  background: rgba(var(--v-theme-on-surface), 0.07);
  outline: none;
}

.vhelp__speed-option.is-active {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

/* Always the same width, so the numbers line up whether or not a row carries the tick. */
.vhelp__speed-check {
  width: 14px;
}

.vhelp-menu-enter-active,
.vhelp-menu-leave-active {
  transition:
    opacity 0.14s ease,
    transform 0.14s ease;
}

.vhelp-menu-enter-from,
.vhelp-menu-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

@media (prefers-reduced-motion: reduce) {
  .vhelp__speed-chevron {
    transition: none;
  }
}
</style>
